import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@environment/environment";
import { ErrorHandlerService } from "@helpers/error-handler.service";
import { ChartLatestReadingVM, LineChartResponse, LogicalGroupVM, ResourceSeriesResponse } from "@models/backend-generated-models/models";
import { throwError } from 'rxjs';
import { catchError } from "rxjs/operators";

@Injectable({ providedIn: 'root' })
export class UserMenuService {
    constructor(private httpClient: HttpClient, private errorHandler: ErrorHandlerService) {
    }

    getUserManualURL$() {
        return this.httpClient.get(`${environment.apiUrl}usermenu`, {
            responseType: 'text'
        }).pipe(
            catchError(
                (error: HttpErrorResponse) => {
                    this.errorHandler.handleError(error);

                    return throwError(error);
                }
            )
        );
    }

    getAggregatedReportURL$() {
        return this.httpClient.get(`${environment.apiUrl}usermenu/aggregated-report`,
            { responseType: 'text' })
            .pipe(
                catchError(
                    (error: HttpErrorResponse) => {
                        this.errorHandler.handleError(error);

                        return throwError(error);
                    }
                )
            );
    }

    getReportViewerURL$() {
        return this.httpClient.get(`${environment.apiUrl}usermenu/report-viewer`,
            { responseType: 'text' })
            .pipe(
                catchError(
                    (error: HttpErrorResponse) => {
                        this.errorHandler.handleError(error);

                        return throwError(error);
                    }
                )
            );
    }

    getReportTicket$(siteId: number, portalId: number, reportPath: string) {
        const params = new HttpParams()
            .set("siteId", siteId.toString())
            .set("portalId", portalId.toString())
            .set("reportPath", reportPath);

        return this.httpClient.get(`${environment.apiUrl}usermenu/ticket`, {
            params,
            responseType: 'text'
        }).pipe(
            catchError(
                (error: HttpErrorResponse) => {
                    this.errorHandler.handleError(error);

                    return throwError(error);
                }
            )
        );
    }

    getRptResourceConsumption$(siteId: number, logicalGroupId: number, period: string, dateLeft: string, dateRight: string) {
        if (!siteId) return throwError('SiteId is required');
        if (!logicalGroupId) return throwError('Select Chart');
        const params = new HttpParams()
            .set("siteId", siteId)
            .set("logicalGroupId", logicalGroupId)
            .set("period", period)
            .set("dateLeft", dateLeft)
            .set("dateRight", dateRight);

        return this.httpClient.get<LineChartResponse>(`${environment.apiUrl}usermenu/dashboard-report`, {
            params
        }).pipe(
            catchError(
                (error: HttpErrorResponse) => {
                    this.errorHandler.handleError(error);

                    return throwError(error);
                }
            )
        );
    }

    /**
     * Every series behind one FPCategory over one window, values SIGNED.
     * Unlike getRptResourceConsumption$ this returns MULTIPLE named series, so mains and solar
     * arrive together and a negative mains value survives as an export hour.
     *
     * reportUrl decides which proc answers. Leave it off and you get the remainder report, which is
     * all this endpoint served before it covered the policy and consumption ones. groupType is the
     * selected group's own parameter name as GroupVM.Type gives it - "aLogicalGroupID", "aPolicyID"
     * or "aPolicyGroupID" - passed through exactly as the SSRS URL passes it.
     */
    /**
     * The Compare view: one total per group for the period starting at leftStart and the one
     * starting at rightStart, each one `period` long ("1m", "1w", ...). Same response shape as
     * getResourceSeries$, two points per series, left then right.
     */
    getResourceComparison$(
        siteId: number,
        readingTypeId: number,
        period: string,
        leftStart: string,
        rightStart: string,
        reportUrl?: string,
        groupType?: string,
        groupId?: number) {
        if (!siteId) return throwError('SiteId is required');
        if (!readingTypeId) return throwError('ReadingTypeId is required');
        let params = new HttpParams()
            .set("siteId", siteId)
            .set("readingTypeId", readingTypeId)
            .set("period", period)
            .set("dateLeft", leftStart)
            .set("dateRight", rightStart);

        if (reportUrl) params = params.set("reportUrl", reportUrl);
        if (groupType) params = params.set("groupType", groupType);
        if (groupId != null) params = params.set("groupId", groupId);

        return this.httpClient.get<ResourceSeriesResponse>(`${environment.apiUrl}usermenu/resource-comparison`, { params });
    }

    /** When the newest reading behind a selection was taken, for opening its chart on it. */
    getLatestReading$(
        siteId: number,
        readingTypeId: number,
        groupType?: string,
        groupId?: number) {
        if (!siteId) return throwError('SiteId is required');
        if (!readingTypeId) return throwError('ReadingTypeId is required');
        let params = new HttpParams()
            .set("siteId", siteId)
            .set("readingTypeId", readingTypeId);

        if (groupType) params = params.set("groupType", groupType);
        if (groupId != null) params = params.set("groupId", groupId);

        return this.httpClient.get<ChartLatestReadingVM>(`${environment.apiUrl}usermenu/latest-reading`, { params });
    }

    getResourceSeries$(
        siteId: number,
        readingTypeId: number,
        period: string,
        dateLeft: string,
        dateRight: string,
        reportUrl?: string,
        groupType?: string,
        groupId?: number) {
        if (!siteId) return throwError('SiteId is required');
        if (!readingTypeId) return throwError('ReadingTypeId is required');
        let params = new HttpParams()
            .set("siteId", siteId)
            .set("readingTypeId", readingTypeId)
            .set("period", period)
            .set("dateLeft", dateLeft)
            .set("dateRight", dateRight);

        if (reportUrl) params = params.set("reportUrl", reportUrl);
        if (groupType) params = params.set("groupType", groupType);
        if (groupId != null) params = params.set("groupId", groupId);

        return this.httpClient.get<ResourceSeriesResponse>(`${environment.apiUrl}usermenu/resource-series`, {
            params
        }).pipe(
            catchError(
                (error: HttpErrorResponse) => {
                    this.errorHandler.handleError(error);

                    return throwError(error);
                }
            )
        );
    }

    getSiteLogicalGroups$(siteId: number) {
        if (!siteId) return throwError('SiteId is required');
        const params = new HttpParams().set("siteId", siteId);
        return this.httpClient.get<LogicalGroupVM[]>(`${environment.apiUrl}usermenu/site-logical-groups`, {
            params
        }).pipe(
            catchError(
                (error: HttpErrorResponse) => {
                    this.errorHandler.handleError(error);

                    return throwError(error);
                }
            )
        );
    }

    getLatestReadings$(siteId: number) {
        if (!siteId) return throwError('SiteId is required');
        const params = new HttpParams().set("siteId", siteId);
        return this.httpClient.get<CategoryLatestReadingsVM[]>(`${environment.apiUrl}usermenu/latest-readings`, {
            params
        }).pipe(
            catchError(
                (error: HttpErrorResponse) => {
                    this.errorHandler.handleError(error);

                    return throwError(error);
                }
            )
        );
    }
}

export interface CategoryLatestReadingsVM {
    fpcategoryId: number;
    categoryName: string;
    readings: LogicalGroupReadingVM[];
}

export interface LogicalGroupReadingVM {
    logicalGroupId: number;
    name: string;
    readingTypeId: number | null;
    unit: string | null;
    value: number | null;
    timestamp: string | null;
    isStale: boolean;
    isIncomplete: boolean;
    expectedChannelCount: number;
    availableChannelCount: number;
}
